import { notification } from 'antd';
import { history } from '@umijs/max';
import {
  fetchNoticePageList,
  getOrderNoFromNotice,
  markNoticeAsRead,
  type NoticeItem,
} from '@/api/notice';
import httpBaseConfig from '@/api/base/httpBaseConfig';
import { getTokenFromStorage, getUserIdFromStorage } from '@/global/CommonUtils';

const RECONNECT_DELAY_MS = 5000;
const MAX_RECONNECT_DELAY_MS = 60000;
const HEARTBEAT_INTERVAL_MS = 30000;
const HEARTBEAT_TIMEOUT_MS = 60000;
/** 客户端心跳包，服务端以纯文本 pong 响应 */
const HEARTBEAT_MESSAGE = 'ping';
const HEARTBEAT_RESPONSE = 'pong';
const WS_CONNECTED_MESSAGE = '连接成功';

type WsMessageType = 'heartbeat' | 'connected' | 'notice';

interface ParsedWsMessage {
  type: WsMessageType;
  content: string;
}

function parseWsMessage(data: unknown): ParsedWsMessage | null {
  if (typeof data !== 'string') return null;
  const content = data.trim();
  if (!content) return null;

  const lower = content.toLowerCase();
  if (lower === HEARTBEAT_RESPONSE || lower === 'heartbeat') {
    return { type: 'heartbeat', content };
  }
  if (content === WS_CONNECTED_MESSAGE) {
    return { type: 'connected', content };
  }

  try {
    const json = JSON.parse(content) as { type?: string; cmd?: string; msg?: string };
    const type = json?.type?.toLowerCase();
    const cmd = json?.cmd?.toLowerCase();
    if (type === HEARTBEAT_RESPONSE || type === 'heartbeat' || cmd === HEARTBEAT_RESPONSE) {
      return { type: 'heartbeat', content };
    }
    if (type === 'connected' || json?.msg === WS_CONNECTED_MESSAGE) {
      return { type: 'connected', content: json?.msg || content };
    }
  } catch {
    // 非 JSON，按业务通知处理
  }

  return { type: 'notice', content };
}

/** WebSocket 直连域名，不走 admin-gateway */
function getWsOrigin() {
  const url = new URL(httpBaseConfig.baseUrl);
  const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${url.host}`;
}

function buildWebSocketUrl(userId: string | number, token: string) {
  const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  const params = new URLSearchParams({
    Authorization: authToken,
    userId: String(userId),
  });
  return `${getWsOrigin()}/websocket/api-service/server/${userId}?${params.toString()}`;
}

function getNoticeTitle(item: NoticeItem): string {
  return item.title || item.noticeTitle || '新消息';
}

function getNoticeContent(item: NoticeItem): string {
  return item.content || item.noticeContent || '';
}

function isUnread(item: NoticeItem): boolean {
  return String(item.status) === '0';
}

class NoticeWebSocketService {
  private ws: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private heartbeatTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectDelay = RECONNECT_DELAY_MS;
  private manualClose = false;
  private serverReady = false;
  private awaitingPong = false;
  private shownNoticeIds = new Set<number>();
  private handledNoticeIds = new Set<number>();
  private fetching = false;

  connect() {
    const token = getTokenFromStorage();
    const userId = getUserIdFromStorage();

    if (!token || !userId || userId === 'undefined') {
      return;
    }

    this.disconnect(false);
    this.manualClose = false;

    const url = buildWebSocketUrl(userId, token);
    const ws = new WebSocket(url);
    this.ws = ws;

    ws.onopen = () => {
      this.reconnectDelay = RECONNECT_DELAY_MS;
      this.serverReady = false;
    };

    ws.onmessage = (event) => {
      console.log('[WebSocket 收到]', event.data);
      this.handleWsMessage(event.data);
    };

    ws.onerror = (event) => {
      console.error('WebSocket 连接异常:', event);
      ws.close();
    };

    ws.onclose = (event) => {
      console.warn('WebSocket 已关闭:', event.code, event.reason);
      this.serverReady = false;
      this.stopHeartbeat();
      this.ws = null;
      if (!this.manualClose) {
        this.scheduleReconnect();
      }
    };
  }

  disconnect(clearShown = true) {
    this.manualClose = true;
    this.serverReady = false;
    this.stopHeartbeat();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    if (clearShown) {
      this.shownNoticeIds.clear();
      this.handledNoticeIds.clear();
      notification.destroy();
    }
  }

  private scheduleReconnect() {
    if (this.reconnectTimer) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, this.reconnectDelay);
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, MAX_RECONNECT_DELAY_MS);
  }

  private startHeartbeat() {
    this.stopHeartbeat(false);
    this.sendHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      this.sendHeartbeat();
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(resetAwaitingPong = true) {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    this.clearHeartbeatTimeout();
    if (resetAwaitingPong) {
      this.awaitingPong = false;
    }
  }

  private clearHeartbeatTimeout() {
    if (this.heartbeatTimeoutTimer) {
      clearTimeout(this.heartbeatTimeoutTimer);
      this.heartbeatTimeoutTimer = null;
    }
  }

  private onHeartbeatResponse() {
    this.awaitingPong = false;
    this.clearHeartbeatTimeout();
  }

  private handleWsMessage(data: unknown) {
    const parsed = parseWsMessage(data);
    if (!parsed) return;

    switch (parsed.type) {
      case 'heartbeat':
        this.onHeartbeatResponse();
        break;
      case 'connected':
        if (!this.serverReady) {
          this.serverReady = true;
          this.startHeartbeat();
        }
        this.fetchAndShowUnreadNotices();
        break;
      case 'notice':
        this.onHeartbeatResponse();
        this.fetchAndShowUnreadNotices();
        break;
      default:
        break;
    }
  }

  private sendHeartbeat() {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    if (this.awaitingPong) {
      console.warn('WebSocket 心跳超时，断开重连');
      this.ws.close();
      return;
    }

    this.awaitingPong = true;
    console.log('[WebSocket 发送]', HEARTBEAT_MESSAGE);
    this.ws.send(HEARTBEAT_MESSAGE);

    this.heartbeatTimeoutTimer = setTimeout(() => {
      if (this.awaitingPong && this.ws) {
        console.warn('WebSocket 心跳未响应，断开重连');
        this.ws.close();
      }
    }, HEARTBEAT_TIMEOUT_MS);
  }

  private async fetchAndShowUnreadNotices() {
    if (this.fetching) return;
    this.fetching = true;
    try {
      const result = await fetchNoticePageList({
        status: '0',
        clientUserId: getUserIdFromStorage(),
        noticeType: '1',
      });
      const records = result?.data?.records || [];
      records.filter(isUnread).forEach((item) => this.showNotice(item));
    } catch (error) {
      console.error('获取消息列表失败:', error);
    } finally {
      this.fetching = false;
    }
  }

  private showNotice(item: NoticeItem) {
    const noticeId = item.noticeId;
    if (!noticeId || this.shownNoticeIds.has(noticeId)) {
      return;
    }
    this.shownNoticeIds.add(noticeId);

    notification.info({
      key: `notice-${noticeId}`,
      message: getNoticeTitle(item),
      description: getNoticeContent(item),
      duration: 0,
      placement: 'topRight',
      className: 'notice-clickable',
      style: { cursor: 'pointer' },
      onClick: () => {
        this.handleNoticeClick(item);
      },
      onClose: () => {
        // 点击右上角关闭：标记已读，不跳转
        this.markNoticeReadOnce(noticeId);
      },
    });
  }

  private async handleNoticeClick(item: NoticeItem) {
    const noticeId = item.noticeId;
    if (!noticeId) return;

    notification.destroy(`notice-${noticeId}`);
    await this.markNoticeReadOnce(noticeId);

    const orderNo = getOrderNoFromNotice(item);
    if (orderNo) {
      history.push(`/api/order?orderNo=${encodeURIComponent(orderNo)}`);
      return;
    }
    history.push('/api/order');
  }

  private async markNoticeReadOnce(noticeId: number) {
    if (this.handledNoticeIds.has(noticeId)) {
      return;
    }
    this.handledNoticeIds.add(noticeId);
    try {
      await markNoticeAsRead(noticeId);
    } catch (error) {
      console.error('标记消息已读失败:', error);
      this.handledNoticeIds.delete(noticeId);
    }
  }
}

export const noticeWebSocketService = new NoticeWebSocketService();
